import React, { useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { uploadInChunks } from '../../utils/chunkedUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full px-4 py-2 border border-slate-200 rounded focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 outline-none font-body text-sm';

const FILES = [
  { key: 'thumbnail_url', label: 'Thumbnail Image', accept: 'image/*', folder: 'genz-thumbnails' },
  { key: 'video_url', label: 'Video (MP4)', accept: 'video/mp4,video/*', folder: 'genz-videos' },
  { key: 'audio_url', label: 'Audio (MP3)', accept: 'audio/mpeg,audio/*', folder: 'genz-audio' },
  { key: 'pdf_url', label: 'PDF', accept: '.pdf,application/pdf', folder: 'genz-pdfs' },
];

const Field = ({ label, children }) => (
  <div>
    <label className="block font-body text-sm font-medium text-slate-700 mb-2">{label}</label>
    {children}
  </div>
);

const FileRow = ({ field, value, progress, onPick, onRemove }) => (
  <Field label={field.label}>
    <p className="font-body text-xs text-slate-500 mb-2" data-testid={`genz-current-${field.key}`}>
      {value ? 'A file is connected. Choose a new file to replace it.' : 'No file connected — nothing will show on the website.'}
    </p>
    <input type="file" accept={field.accept} onChange={(e) => e.target.files[0] && onPick(e.target.files[0])} className="w-full px-4 py-2 border border-slate-200 rounded font-body text-sm" data-testid={`genz-${field.key}-input`} />
    {progress != null && progress < 100 && (
      <div className="mt-2 h-1.5 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-brand-accent transition-[width] duration-300" style={{ width: `${progress}%` }} /></div>
    )}
    {value && (
      <button type="button" onClick={onRemove} className="mt-2 font-body text-xs text-red-600 hover:underline" data-testid={`genz-remove-${field.key}`}>
        Remove {field.label}
      </button>
    )}
  </Field>
);

export const GenZLessonModal = ({ lesson, onClose, onSaved }) => {
  const isNew = !lesson.id;
  const [form, setForm] = useState({
    title: lesson.title || '', short_description: lesson.short_description || '', thesis: lesson.thesis || '',
    references: lesson.references || '', overview: (lesson.overview || []).join('\n\n'),
    thumbnail_url: lesson.thumbnail_url || null, video_url: lesson.video_url || null,
    audio_url: lesson.audio_url || null, pdf_url: lesson.pdf_url || null, published: lesson.published ?? false,
  });
  const [progress, setProgress] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const uploading = Object.values(progress).some((p) => p < 100);

  const pick = async (field, file) => {
    setProgress((p) => ({ ...p, [field.key]: 0 }));
    try {
      const url = await uploadInChunks(file, field.folder, (pct) => setProgress((p) => ({ ...p, [field.key]: pct })));
      set(field.key, url);
      toast.success(`${field.label} uploaded`);
    } catch {
      setProgress((p) => ({ ...p, [field.key]: undefined }));
      toast.error(`${field.label} upload failed`);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, overview: form.overview.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean) };
    try {
      if (isNew) await axios.post(`${API}/admin/genz/lessons`, payload, { withCredentials: true });
      else await axios.put(`${API}/admin/genz/lessons/${lesson.id}`, payload, { withCredentials: true });
      toast.success('Lesson saved');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save lesson');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6" data-testid="genz-lesson-modal">
      <div className="bg-white rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 flex justify-between items-center">
          <h3 className="font-heading text-xl font-medium text-brand-primary">{isNew ? 'Add Lesson' : `Edit Lesson ${lesson.order}`}</h3>
          <button onClick={onClose} className="text-slate-600 hover:text-slate-900" data-testid="genz-modal-close"><X className="h-6 w-6" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Title"><input className={inputCls} value={form.title} required onChange={(e) => set('title', e.target.value)} data-testid="genz-title-input" /></Field>
          <Field label="Short description (lesson card)"><textarea className={`${inputCls} resize-none`} rows={3} value={form.short_description} required onChange={(e) => set('short_description', e.target.value)} data-testid="genz-short-description-input" /></Field>
          <Field label="Thesis (optional)"><input className={inputCls} value={form.thesis} onChange={(e) => set('thesis', e.target.value)} data-testid="genz-thesis-input" /></Field>
          <Field label="Scripture references (optional)"><input className={inputCls} value={form.references} onChange={(e) => set('references', e.target.value)} data-testid="genz-references-input" /></Field>
          <Field label="Full overview (lesson page — blank line between paragraphs)"><textarea className={`${inputCls} resize-y`} rows={6} value={form.overview} onChange={(e) => set('overview', e.target.value)} data-testid="genz-overview-input" /></Field>
          {FILES.map((f) => (
            <FileRow key={f.key} field={f} value={form[f.key]} progress={progress[f.key]} onPick={(file) => pick(f, file)} onRemove={() => set(f.key, null)} />
          ))}
          <label className="flex items-center gap-2 font-body text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={form.published} onChange={(e) => set('published', e.target.checked)} data-testid="genz-published-checkbox" />
            Published (visible on the website)
          </label>
          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 font-body font-medium text-slate-700 hover:text-slate-900" data-testid="genz-modal-cancel">Cancel</button>
            <button type="submit" disabled={saving || uploading} className="px-6 py-2 bg-brand-accent text-white font-body font-medium rounded hover:bg-brand-accent-hover transition-colors duration-300 disabled:opacity-50" data-testid="save-genz-lesson-button">
              {uploading ? 'Uploading...' : saving ? 'Saving...' : 'Save Lesson'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
